import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { platformAdminCache, resolvePlatformAdmin } from '@/lib/platform/usePlatformAdmin';

type Status = 'loading' | 'allow' | 'deny';

export function PlatformGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<Status>(() => {
    if (user?.id && platformAdminCache.has(user.id)) {
      return platformAdminCache.get(user.id) ? 'allow' : 'deny';
    }
    return 'loading';
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setStatus('deny');
      return;
    }
    const key = user.id;
    if (platformAdminCache.has(key)) {
      setStatus(platformAdminCache.get(key) ? 'allow' : 'deny');
      return;
    }
    let cancelled = false;
    resolvePlatformAdmin(key).then((ok) => {
      if (!cancelled) setStatus(ok ? 'allow' : 'deny');
    });
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  if (authLoading || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth/signin?next=/app/platform" replace />;
  }

  if (status === 'deny') {
    return <Navigate to="/app/admin" replace />;
  }

  return <>{children}</>;
}

export default PlatformGuard;
